import { useState, useMemo } from 'react';
import {
  AutocompleteInput,
  required,
  useGetList,
  useTranslate,
} from 'react-admin';
import { Alert, Box, Chip, Typography } from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';

interface ContactIdentity {
  id: number;
  uuid: string;
  contactId: number;
  channelConnectionId: number;
  externalId: string;
  name?: string | null;
  avatarUrl?: string | null;
}

interface ContactChoice {
  id: string; // contactId as string for form compatibility
  name: string;
  contactName: string | null;
  contactId: number;
  contactIdentityId: number;
}

export interface TelegramTicketFormProps {
  channelConnectionId: number | null;
  onContactSelect: (choice: ContactChoice | null) => void;
  selectedContact: ContactChoice | null;
}

/**
 * TelegramTicketForm - Contact search for Telegram ticket creation
 *
 * Only shows contacts that have already messaged this Telegram bot.
 * Telegram bots cannot initiate conversations - users must message first.
 */
export const TelegramTicketForm = ({
  channelConnectionId,
  onContactSelect,
  selectedContact,
}: TelegramTicketFormProps) => {
  const translate = useTranslate();
  const [nameSearch, setNameSearch] = useState('');

  // Fetch contact identities for this channel connection
  // These are contacts that have already talked to this bot
  const { data: identities, isLoading } = useGetList<ContactIdentity>('contact-identity', {
    pagination: { page: 1, perPage: 50 },
    filter: channelConnectionId
      ? {
          channelConnectionId_eq: channelConnectionId,
          isGroup_eq: false, // Only individual chats, not groups
          ...(nameSearch.length >= 2 ? { name_q: nameSearch } : {}),
        }
      : {},
    sort: { field: 'name', order: 'ASC' },
  }, { enabled: !!channelConnectionId });

  // Build choices from contact identities
  const contactChoices = useMemo(() => {
    const choices: ContactChoice[] = [];

    // Always include the selected contact so it displays correctly
    if (selectedContact) {
      choices.push(selectedContact);
    }

    // Add identities as choices
    if (identities) {
      identities.forEach((identity) => {
        // Skip if already selected
        if (selectedContact?.contactIdentityId === identity.id) return;

        const displayName = identity.name || identity.externalId;
        choices.push({
          id: String(identity.contactId),
          name: displayName,
          contactName: identity.name || null,
          contactId: identity.contactId,
          contactIdentityId: identity.id,
        });
      });
    }

    return choices;
  }, [identities, selectedContact]);

  const handleContactChange = (value: string | null) => {
    if (!value) {
      onContactSelect(null);
      return;
    }
    const choice = contactChoices.find((c) => c.id === value);
    if (choice) {
      onContactSelect(choice);
    }
  };

  if (!channelConnectionId) return null;

  const hasNoContacts = !isLoading && identities && identities.length === 0 && nameSearch.length < 2;

  return (
    <>
      <Box mb={2}>
        <Alert
          severity="info"
          icon={<Chip icon={<TelegramIcon />} label="Telegram" color="info" size="small" />}
        >
          {translate('resources.conversation.channelTypes.telegramHelper')}
        </Alert>
      </Box>

      {hasNoContacts ? (
        <Alert severity="warning">
          <Typography variant="body2">
            {translate('resources.conversation.telegram.noContacts')}
          </Typography>
        </Alert>
      ) : (
        <AutocompleteInput
          source="contactId"
          label={translate('resources.conversation.fields.contact')}
          choices={contactChoices}
          optionText="name"
          optionValue="id"
          validate={required()}
          fullWidth
          setFilter={setNameSearch}
          shouldRenderSuggestions={(val: string) => val.length >= 2}
          onChange={handleContactChange}
          helperText={translate('resources.conversation.helpers.telegramContact')}
          isLoading={isLoading}
          noOptionsText={translate('resources.conversation.telegram.noMatchingContacts')}
        />
      )}
    </>
  );
};

export default TelegramTicketForm;
